
import {ITEM_VIEW_TASK, ItemViewType} from "../app-manager/item-view"
import Timer, {TimerState} from "./timer"
import {EntityType} from "../const/item"

// composite key so task ids and meeting ids never collide in the timers map
function timerKey(entityType:EntityType, id:ID) {
    return entityType + ':' + id
}

const LSK_TIMERS = 'task_timers'
const LSK_CLOSE_TIME = 'tt_close_time'
const LSK_DOCUMENTS_COUNTER = 'tt_counter'

function formatTimeLong(ms:number) {
    const seconds = ms/1000
    return moment.unix(seconds).utc(false).format('HH:mm:ss')
}

function formatTimeShort(ms:number) {
    const seconds = ms/1000
    const format = seconds >= 3600 ? 'HH:mm' : 'mm:ss'
    return moment.unix(seconds).utc(false).format(format)
}


let needUpdateTimeAndCounter = false; // flag to save to localStorage window close time and total windows count

// const debug = "TaskTimers"
const debug = false

export default class TaskTimers {

    public timers : any = {} // "entityType:id" => timer

    constructor() {

        // this.timers = {};

        this._initFromStorage(localStorage.getItem(LSK_TIMERS), true);

        window.addEventListener('storage', e => {
            if (e.key !== LSK_TIMERS) return;

            this.timers = {};
            this._initFromStorage(e.newValue) // fires task-timers:updated, which the meeting view listens to

            // try to reload the open task app
            const taskView = gd.itemView.findByContainerId(ItemViewType.ITEM_VIEW_TASK);
            if (!taskView) {
                return
            }

            const hadTaskTimer = this._parseTimersList(e.oldValue).some((ot:any) => {
                return (ot.entityType ?? EntityType.TASK) === EntityType.TASK
                    && (ot.entity || ot.task)?.id === taskView.app.taskId
            })
            if (hadTaskTimer) {
                taskView.app.refresh()
            }
        })
    }

    _parseTimersList(storageValue:any) {
        let list;
        try {
            list = JSON.parse(storageValue);
        } catch(e) {
            return [];
        }
        if (!list || list.length < 1) return [];
        return list;
    }

    _initFromStorage(storageValue:any, initOnLoad=false) {
        let saveNow = false;
        const list = this._parseTimersList(storageValue);

        const taskIds: ID[] = []
        list.forEach((li:any) => {
            const entityType = li.entityType ?? EntityType.TASK          // legacy records had no entityType
            const entityData = li.entity || li.task                      // legacy records stored it under "task"
            const timer = new Timer(entityData, entityType);
            timer.deltaTime = li.time;
            timer.prevReportedTime = li.reported;
            this.timers[timerKey(entityType, entityData.id)] = timer;

            if (li.state === TimerState.TIMER_ACTIVE) {
                timer.startDate = new Date(li.start * 1000);

                if (initOnLoad && documentCounterGet() < 1) {
                    // only one window opened, all others where closed too
                    const closeDate = documentPrevCloseDate();
                    // @ts-ignore
                    const passedTime = new Date() - closeDate;
                    if (passedTime / 60000 > 4*60) {
                        // 4 hours passed, set a timer on pause
                        timer.state = TimerState.TIMER_ACTIVE;
                        timer.pause();
                        saveNow = true;
                    } else {
                        timer.start();
                    }

                } else {
                    // there are other active documents, safe to start timer
                    timer.start();
                }
            } else {
                timer.state = li.state;
            }

            taskIds.push(timer.entityId)
        });
        gd.bus.triggerEvent('task-timers:updated', {taskIds})

        needUpdateTimeAndCounter = list.length > 0;

        if (needUpdateTimeAndCounter) {
            documentsCounterPlus();
        }

        if (saveNow) this._saveToStorage();
    }

    _saveToStorage() {

        let list = [];

        for (let key in this.timers) {
            const t = this.timers[key];
            const entity = {
                id:         t.entityId,
                entityType: t.entityType,
                taskTypeId: t.entity.taskTypeId,
                title:      t.entity.title || t.entity.name,    // tray reads title for both entity types
                name:       t.entity.name,                      // meetings read name after a reload
                companyId:  t.entity.companyId
            }
            list.push({
                state:      t.state,
                start:      t.startDate ? t.startDate.getTime() / 1000 : null,
                time:       t.getTime(),
                reported:   t.prevReportedTime,
                entityType: t.entityType,
                entity:     entity,
                task:       entity     // legacy key, kept so older tabs can still read the record
            });
        }

        localStorage.setItem(LSK_TIMERS, JSON.stringify(list));


        needUpdateTimeAndCounter = list.length > 0;
        if (list.length < 1) {
            localStorage.removeItem(LSK_CLOSE_TIME);
            localStorage.removeItem(LSK_DOCUMENTS_COUNTER);
        }
    }

    _addTimer(entity:any, entityType:EntityType = EntityType.TASK) {
        const key = timerKey(entityType, entity.id)
        if (!this.timers[key]) {
            this.timers[key] = new Timer(entity, entityType);
        }
    }

    getTimer(id?:ID, entityType:EntityType = EntityType.TASK):Timer | null {
        if (!id) return null

        return this.timers[timerKey(entityType, id)]
    }

    isTimerActive(id:ID, entityType:EntityType = EntityType.TASK) {
        const timer = this.getTimer(id, entityType);
        if (timer) return timer.isActive();
        return false;
    }

    startTimer(entity:any, entityType:EntityType = EntityType.TASK) {
        const taskIds = this._pauseAll()
        this._addTimer(entity, entityType)
        const timer = this.getTimer(entity.id, entityType)!
        timer.start()

        taskIds.push(entity.id)
        gd.bus.triggerEvent('task-timers:updated', {taskIds})

        this._saveToStorage()
    }

    _pauseAll() {
        const taskIds = []
        for (let key in this.timers) {
            const timer = this.timers[key]
            taskIds.push(timer.entityId)
            timer.pause()
        }

        return taskIds
    }

    pauseTimer(id:ID, entityType:EntityType = EntityType.TASK) {
        const timer = this.getTimer(id, entityType)
        if (!timer) return

        timer.pause()
        gd.bus.triggerEvent('task-timers:updated', {taskIds: [id]})
        this._saveToStorage()
    }

    removeTimer(id:ID, entityType:EntityType = EntityType.TASK) {
        const timer = this.getTimer(id, entityType)
        if (!timer) return
        timer.destroy()
        delete this.timers[timerKey(entityType, id)]
        gd.bus.triggerEvent('task-timers:updated', {taskIds: [id]})
        this._saveToStorage()
    }

    exportToMenuItems() {
        const list = [];
        for (let key in this.timers) {
            const timer = this.timers[key];
            if (timer.entity.companyId === gd.companyId || !timer.entity.companyId /*for compatibility with older format without companyId*/)
                list.push(timer);
        }
        return list;
    }

    isEmpty() {
        return (!this.timers || _.isEmpty(this.timers));
    }

    formatTimeShort(ms:number) {
        return formatTimeShort(ms);
    }

    formatTimeLong(ms:number) {
        return formatTimeLong(ms);
    }

    // timer actions
    toggleTimer(entity:any, entityType:EntityType = EntityType.TASK) {
        if (this.isTimerActive(entity.id, entityType)) {
            this.pauseTimer(entity.id, entityType)
            return
        }

        this.startTimer(entity, entityType)
    }

    stopTimer(id?:ID, baseDate?:any, callback?:callback, entityType:EntityType = EntityType.TASK) {
        debug && console.log(debug, id, baseDate)
        if (!id) return

        const timer = this.getTimer(id, entityType)
        if (!timer) return

        this.pauseTimer(id, entityType)
        const minDate = baseDate ? baseDate.subtract(1, 'month') : moment().subtract(1, 'month')

        gd.modals.myTime.submitTimer(
            timer,
            minDate,
            ()=>{
                this.removeTimer(id, entityType)
                if (callback) callback()
            }
        )
    }

    cancelTimer = (id?:ID, callback?:callback, entityType:EntityType = EntityType.TASK) => {
        if (!id) return

        const timer = this.getTimer(id, entityType)
        if (!timer) return

        if (timer.getTime() < 1) {
            // safe to remove
            this.removeTimer(id, entityType)
        } else {
            gd.modals.confirm.genericText("Cancel Timer","You are about to cancel a timer without submitting a time report. Are you sure ?",()=>{
                this.removeTimer(id, entityType)
                gd.bus.triggerEvent('task-view:time-tracking')
                gd.modal.hide()
                if (callback) callback()
            });
        }
    }
}

function documentPrevCloseDate() {
    // @ts-ignore
    let time = parseInt(localStorage.getItem(LSK_CLOSE_TIME));
    if (isNaN(time)) return new Date();

    return new Date(time);
}

function documentCounterGet() {
    // @ts-ignore
    let counter = parseInt(localStorage.getItem(LSK_DOCUMENTS_COUNTER));
    if (isNaN(counter)) counter = 0;
    return counter;
}

function documentsCounterPlus() {
    let counter = documentCounterGet();
    counter++;
    // @ts-ignore
    localStorage.setItem(LSK_DOCUMENTS_COUNTER, counter);
}

function documentsCounterMinus() {
    let counter = documentCounterGet();
    counter--;
    if (counter < 0) counter = 0;
    // @ts-ignore
    localStorage.setItem(LSK_DOCUMENTS_COUNTER, counter);
}


window.addEventListener('unload', () => {
    if (needUpdateTimeAndCounter) {
        // @ts-ignore
        localStorage.setItem(LSK_CLOSE_TIME, (new Date()).getTime());
        documentsCounterMinus();
    }
});
